import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import useSiteData from '../../hooks/useSiteData';
import BusinessHours from '../../components/BusinessHours';

const EditBusinessInfo = () => {
  const socialPlatforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      hoverColor: 'hover:text-[#1877F2]', // Facebook blue
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
        </svg>
      )
    },
    {
      id: 'instagram',
      name: 'Instagram',
      hoverColor: 'hover:text-[#E4405F]', // Instagram pink
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
        </svg>
      )
    },
    {
      id: 'yelp',
      name: 'Yelp',
      hoverColor: 'hover:text-[#FF1A1A]', // Yelp red
      icon: (
        <svg className='h-5 w-5' fill="currentColor"  viewBox="0 0 32 32">
          <path d="M11.876,19.928l1.381-.319c.029-.007,.077-.018,.136-.037,.381-.102,.708-.349,.911-.688,.203-.339,.267-.743,.179-1.128-.002-.008-.004-.016-.006-.025-.045-.184-.125-.359-.234-.514-.155-.196-.346-.361-.564-.484-.256-.146-.524-.27-.801-.371l-1.515-.553c-.85-.316-1.7-.624-2.558-.925-.556-.198-1.028-.372-1.437-.498-.077-.024-.163-.048-.232-.072-.495-.152-.843-.215-1.138-.217-.197-.007-.393,.029-.574,.105-.189,.082-.359,.202-.499,.354-.069,.079-.135,.161-.195,.247-.057,.087-.109,.178-.157,.271-.052,.1-.097,.203-.134,.309-.281,.831-.421,1.703-.414,2.58,.005,.793,.026,1.811,.462,2.501,.105,.177,.246,.33,.414,.449,.31,.214,.624,.243,.951,.266,.488,.035,.96-.085,1.432-.194l4.589-1.06h.004Z"></path><path d="M27.288,12.594c-.377-.792-.884-1.514-1.5-2.138-.08-.079-.165-.152-.255-.219-.083-.063-.17-.121-.26-.174-.092-.05-.187-.096-.284-.136-.192-.075-.397-.109-.602-.1-.197,.011-.388,.065-.562,.158-.263,.131-.548,.341-.928,.694-.052,.052-.118,.111-.177,.166-.313,.294-.661,.657-1.075,1.079-.64,.647-1.27,1.297-1.896,1.954l-1.12,1.162c-.205,.212-.392,.442-.558,.685-.142,.206-.242,.438-.295,.682-.031,.187-.026,.379,.013,.565,.002,.008,.004,.016,.006,.025,.088,.385,.323,.72,.653,.935,.331,.215,.732,.294,1.12,.219,.061-.009,.109-.019,.138-.027l5.973-1.38c.471-.109,.948-.208,1.371-.454,.284-.164,.553-.328,.738-.656,.099-.181,.159-.38,.176-.585,.092-.814-.333-1.738-.675-2.454Z"></path><path d="M16.598,15.106c.433-.544,.432-1.354,.47-2.017,.13-2.214,.267-4.428,.376-6.643,.042-.839,.132-1.667,.082-2.512-.041-.697-.046-1.498-.486-2.07-.777-1.009-2.435-.926-3.565-.77-.346,.048-.693,.113-1.038,.195s-.685,.173-1.018,.28c-1.085,.355-2.61,1.008-2.869,2.258-.146,.707,.199,1.43,.467,2.075,.324,.782,.768,1.486,1.172,2.222,1.068,1.943,2.156,3.874,3.24,5.807,.324,.577,.677,1.308,1.304,1.607,.041,.018,.084,.034,.127,.048,.281,.106,.587,.127,.88,.059,.018-.004,.035-.008,.052-.012,.27-.074,.515-.221,.707-.425,.035-.033,.068-.067,.099-.103Z"></path><path d="M16.079,21.033c-.169-.238-.409-.416-.685-.509-.276-.093-.575-.096-.853-.009-.065,.022-.129,.048-.191,.079-.095,.048-.186,.105-.271,.169-.249,.184-.459,.424-.649,.664-.048,.061-.093,.143-.15,.195l-.961,1.321c-.545,.74-1.082,1.483-1.614,2.237-.347,.487-.648,.898-.885,1.261-.045,.069-.092,.145-.135,.206-.285,.44-.446,.761-.528,1.048-.062,.19-.082,.392-.058,.59,.026,.207,.096,.406,.204,.584,.058,.089,.12,.176,.186,.259,.069,.08,.142,.156,.219,.228,.082,.079,.17,.151,.264,.216,.663,.461,1.389,.793,2.153,1.049,.635,.211,1.296,.337,1.964,.375,.114,.006,.228,.003,.341-.007,.105-.009,.21-.024,.313-.044,.104-.024,.206-.054,.306-.089,.195-.073,.373-.186,.522-.332,.141-.141,.249-.311,.318-.498,.111-.277,.184-.628,.232-1.15,.004-.074,.015-.163,.022-.245,.038-.433,.055-.942,.083-1.539,.047-.92,.084-1.835,.113-2.753,0,0,.062-1.632,.062-1.632,.014-.376,.003-.793-.102-1.167-.046-.179-.12-.35-.219-.507h0Z"></path><path d="M26.924,23.587c-.2-.22-.485-.439-.933-.71-.065-.036-.14-.084-.21-.126-.373-.224-.823-.46-1.348-.746-.807-.443-1.614-.875-2.428-1.303l-1.44-.763c-.075-.022-.15-.076-.221-.11-.277-.132-.57-.256-.874-.312-.105-.02-.211-.031-.318-.033-.069,0-.138,.003-.206,.012-.288,.045-.554,.179-.761,.384-.207,.205-.344,.47-.392,.758-.022,.183-.015,.368,.02,.549,.07,.383,.241,.762,.418,1.094l.769,1.441c.428,.813,.861,1.62,1.304,2.426,.286,.525,.525,.975,.748,1.347,.042,.07,.09,.146,.126,.21,.272,.448,.49,.73,.711,.932,.143,.138,.315,.244,.503,.309,.198,.066,.407,.089,.615,.068,.105-.012,.21-.031,.314-.054,.102-.027,.203-.06,.301-.098,.106-.04,.21-.088,.31-.143,.584-.328,1.123-.731,1.602-1.199,.575-.565,1.082-1.182,1.477-1.888,.055-.1,.102-.204,.142-.31,.037-.099,.069-.199,.096-.301,.023-.104,.041-.209,.054-.314,.02-.207-.004-.416-.071-.614-.065-.189-.171-.362-.31-.505h0Z"/>
        </svg>
      )
    },
    {
      id: 'tripAdvisor',
      name: 'Tripadvisor',
      hoverColor: 'hover:text-green-700', // TripAdvisor green
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12.006 4.295c-2.67 0-5.338.467-7.276 1.395C3.386 6.323 2.05 7.164 2.05 7.164L2 7.19v.032c0 .75.614 1.365 1.365 1.365.307 0 .593-.103.82-.277.076.102.102.205.224.31 1.55 2.193 4.083 3.636 6.963 3.636 2.88 0 5.412-1.443 6.963-3.636.074-.105.148-.208.224-.31.227.174.513.277.82.277.751 0 1.365-.615 1.365-1.365v-.032l-.05-.026s-1.336-.84-2.694-1.474c-1.938-.928-4.606-1.395-7.276-1.395v.001zm0 2.726c1.82 0 3.297 1.476 3.297 3.296 0 1.82-1.477 3.297-3.297 3.297-1.82 0-3.297-1.477-3.297-3.297 0-1.82 1.477-3.296 3.297-3.296zm0 .973c-1.28 0-2.323 1.043-2.323 2.323s1.043 2.323 2.323 2.323 2.323-1.043 2.323-2.323-1.043-2.323-2.323-2.323zm-6.314.014c1.82 0 3.297 1.476 3.297 3.296 0 1.82-1.477 3.297-3.297 3.297-1.82 0-3.297-1.477-3.297-3.297 0-1.82 1.477-3.296 3.297-3.296zm12.628 0c1.82 0 3.297 1.476 3.297 3.296 0 1.82-1.477 3.297-3.297 3.297-1.82 0-3.297-1.477-3.297-3.297 0-1.82 1.477-3.296 3.297-3.296zm-12.628.973c-1.28 0-2.323 1.043-2.323 2.323s1.043 2.323 2.323 2.323 2.323-1.043 2.323-2.323-1.043-2.323-2.323-2.323zm12.628 0c-1.28 0-2.323 1.043-2.323 2.323s1.043 2.323 2.323 2.323 2.323-1.043 2.323-2.323-1.043-2.323-2.323-2.323z"/>
        </svg>
      )
    }
  ];
  const [formData, setFormData] = useState({
    businessName: '',
    logoUrl: '',
    contact: {
      email: '',
      googleMapsLocation: '',
      businessAddress: '',
      businessHours: [],
      contactNumber: '',
      faxNumber: ''
    },
    socialMedia: {
      facebook: { url: '', enable: false },
      instagram: { url: '', enable: false },
      yelp: { url: '', enable: false },
      tripAdvisor: { url: '', enable: false }
    }
  });
  
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { refetch } = useSiteData();

  // Load initial data
  useEffect(() => {
    const loadBusinessData = async () => {
      try {
        const docRef = doc(db, "business", "singleton");
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setFormData(docSnap.data());
        }
      } catch (error) {
        console.error('Error loading business data:', error);
        setError('Failed to load business information.');
      }
    };

    loadBusinessData();
  }, []);

  const handleInputChange = (section, field, value) => {
    if (section === 'root') {
      setFormData(prev => ({ ...prev, [field]: value }));
    } else if (section === 'contact') {
      setFormData(prev => ({
        ...prev,
        contact: { ...prev.contact, [field]: value }
      }));
    } else if (section === 'socialMedia') {
      const [platform, attribute] = field.split('.');
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [platform]: {
            ...prev.socialMedia[platform],
            [attribute]: attribute === 'enable' ? !prev.socialMedia[platform][attribute] : value
          }
        }
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const docRef = doc(db, "business", "singleton");
      await setDoc(docRef, formData, { merge: true });
      
      toast.success('Business information updated successfully!');
      setError('');
      setIsLoading(false);
      setIsEditing(false);
      setShowPreview(false);
      refetch();
    } catch (error) {
      console.error('Error updating document:', error);
      setError('Failed to update business information.');
      toast.error('Failed to update business information.');
      setIsLoading(false);
    }
  };
  
  // Function to format platform name
  const formatPlatformName = (platform) => {
    if (platform === 'tripAdvisor') return 'TripAdvisor';
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  // Social media platforms data
  
  const renderReadOnlyMode = () => (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Business Information</h2>
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
          >
            <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
            Edit
          </button>
        </div>
        
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Business Name</h3>
              <p className="mt-1 text-gray-900">{formData.businessName}</p>
            </div>
            {formData.logoUrl && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Logo</h3>
                <div className="mt-2">
                  <img 
                    src={formData.logoUrl} 
                    alt="Business Logo" 
                    className="h-20 object-contain"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Email</h4>
                <p className="mt-1 text-gray-900">{formData.contact.email}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Contact Number</h4>
                <p className="mt-1 text-gray-900">{formData.contact.contactNumber}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Fax Number</h4>
                <p className="mt-1 text-gray-900">{formData.contact.faxNumber}</p>
              </div>
              <div className="md:col-span-2">
                <h4 className="text-sm font-medium text-gray-500">Business Address</h4>
                <p className="mt-1 text-gray-900">{formData.contact.businessAddress}</p>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Business Hours</h3>
            <div className="space-y-2">
              {formData.contact.businessHours.map((hours, index) => (
                <p key={index} className="text-gray-900">{hours}</p>
              ))}
            </div>
          </div>

          {/* Social Media */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Social Media</h3>
            <div className="space-y-3">
              {socialPlatforms.map(platform => {
                const socialData = formData.socialMedia[platform.id];
                if (!socialData?.enable) return null;
                return (
                  <div key={platform.id} className="flex items-center space-x-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full ${platform.hoverColor}`}>
                      {platform.icon}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{platform.name}</p>
                      <a 
                        href={socialData.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {socialData.url}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-6">Business Information</h1>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6 flex items-center animate-fade-in">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
        
        {!showPreview ? (
          isEditing ? (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name
                    </label>
                    <input
                      type="text"
                      value={formData.businessName}
                      onChange={(e) => handleInputChange('root', 'businessName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.contact.email}
                      onChange={(e) => handleInputChange('contact', 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Number
                    </label>
                    <input
                      type="text"
                      value={formData.contact.contactNumber}
                      onChange={(e) => handleInputChange('contact', 'contactNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fax Number
                    </label>
                    <input
                      type="text"
                      value={formData.contact.faxNumber}
                      onChange={(e) => handleInputChange('contact', 'faxNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Address
                    </label>
                    <input
                      type="text"
                      value={formData.contact.businessAddress}
                      onChange={(e) => handleInputChange('contact', 'businessAddress', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Google Maps Location
                    </label>
                    <input
                      type="text"
                      value={formData.contact.googleMapsLocation}
                      onChange={(e) => handleInputChange('contact', 'googleMapsLocation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-xl font-semibold mb-4">Business Hours</h2>
                <BusinessHours
                  hours={formData.contact.businessHours}
                  onChange={(newHours) => {
                    setFormData(prev => ({
                      ...prev,
                      contact: {
                        ...prev.contact,
                        businessHours: newHours
                      }
                    }));
                  }}
                />
              </div>

              {/* Social Media */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-xl font-semibold mb-4">Social Media</h2>
                <div className="space-y-3">
                  {socialPlatforms.map(platform => (
                    <div 
                      key={platform.id} 
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center flex-1 space-x-4">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-full 
                          ${formData.socialMedia[platform.id]?.enable 
                            ? `${platform.hoverColor} text-gray-700` 
                            : 'text-gray-400 bg-gray-100'}`}
                        >
                          {platform.icon}
                        </div>
                        <div className="flex-1 space-y-1">
                          <label className="block text-sm font-medium text-gray-900">
                            {platform.name}
                          </label>
                          <input
                            type="text"
                            value={formData.socialMedia[platform.id]?.url || ''}
                            placeholder={`Enter ${platform.name} URL`}
                            onChange={(e) => handleInputChange('socialMedia', `${platform.id}.url`, e.target.value)}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
                              focus:ring-2 focus:ring-blue-500 focus:border-transparent
                              ${!formData.socialMedia[platform.id]?.enable ? 'bg-gray-50 text-gray-500' : ''}`}
                            disabled={!formData.socialMedia[platform.id]?.enable}
                          />
                        </div>
                      </div>
                      <div className="ml-4">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.socialMedia[platform.id]?.enable || false}
                            onChange={() => handleInputChange('socialMedia', `${platform.id}.enable`)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer 
                            peer-focus:ring-4 peer-focus:ring-blue-300 
                            peer-checked:after:translate-x-full peer-checked:after:border-white 
                            after:content-[''] after:absolute after:top-0.5 after:left-[2px] 
                            after:bg-white after:border-gray-300 after:border after:rounded-full 
                            after:h-5 after:w-5 after:transition-all 
                            peer-checked:bg-blue-600">
                          </div>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Update Action Buttons */}
              <div className="flex justify-end space-x-4 sticky bottom-0 bg-gray-50 py-4">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  Cancel
                </button>
                <button
                  onClick={() => setShowPreview(true)}
                  className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Preview Changes
                </button>
              </div>
            </div>
          ) : (
            renderReadOnlyMode()
          )
        ) : (
          /* Preview Modal */
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Preview Changes
                </h3>
                <div className="mt-2 max-h-[60vh] overflow-y-auto">
                  {/* Preview Content */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium">Business Name</h4>
                      <p>{formData.businessName}</p>
                    </div>
                    <div>
                      <h4 className="font-medium">Contact Information</h4>
                      <p>Email: {formData.contact.email}</p>
                      <p>Address: {formData.contact.businessAddress}</p>
                      <p>Phone: {formData.contact.contactNumber}</p>
                      <p>Fax: {formData.contact.faxNumber}</p>
                    </div>
                    <div>
                      <h4 className="font-medium">Business Hours</h4>
                      {formData.contact.businessHours.map((hours, index) => (
                        <p key={index}>{hours}</p>
                      ))}
                    </div>
                    <div>
                      <h4 className="font-medium">Social Media</h4>
                      {Object.entries(formData.socialMedia).map(([platform, data]) => (
                        <div key={platform}>
                          <p>
                            {platform}: {data.enable ? 'Enabled' : 'Disabled'} - {data.url}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Edit More
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {isLoading ? 'Saving...' : 'Confirm & Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditBusinessInfo;
